/**
 * The identifiers the whole product is scoped by, and the context a request
 * carries them in.
 *
 * These are branded strings rather than plain ones. The cost is one cast at
 * each boundary where an id enters the system; the return is that a function
 * taking a `WorkspaceId` cannot be handed a `UserId` by mistake — which, in a
 * multi-tenant product, is the mistake that shows one company another's book.
 *
 * Deliberately small. The database schema does not exist yet, and inventing it
 * as TypeScript interfaces first would mean maintaining two versions of the
 * truth. Everything here is what Sessions 2–5 genuinely need to compile
 * against.
 */

import type { Capability } from './capabilities.js';

declare const brand: unique symbol;
type Branded<T, B extends string> = T & { readonly [brand]: B };

/** A company. The tenant boundary; `workspaces.id`. */
export type WorkspaceId = Branded<string, 'WorkspaceId'>;
/** A person's login. `auth.users.id` — one per human, across all companies. */
export type UserId = Branded<string, 'UserId'>;
/** A person *inside one company*. `workspace_members.id` — what work is assigned to. */
export type MemberId = Branded<string, 'MemberId'>;
/** A named bundle of capabilities inside one company. `workspace_roles.id`. */
export type RoleId = Branded<string, 'RoleId'>;

/**
 * Casts at the edge. Every one of these marks a place where a plain string
 * from the network, the URL or the database becomes an identifier we trust —
 * so they are worth being able to grep for.
 */
export const asWorkspaceId = (value: string): WorkspaceId => value as WorkspaceId;
export const asUserId = (value: string): UserId => value as UserId;
export const asMemberId = (value: string): MemberId => value as MemberId;
export const asRoleId = (value: string): RoleId => value as RoleId;

/**
 * What someone is allowed to do inside one company.
 *
 * The list, the areas it groups into and the `Capability` union all live in
 * `capabilities.ts`, where the union is derived from the list so the two cannot
 * disagree. The authoritative copy is the database's `platform_capabilities`,
 * and `tests-integration/capabilities.test.ts` fails if the two sets drift in
 * either direction.
 */

/** One person's standing in one company. */
export interface WorkspaceMembership {
    memberId: MemberId;
    workspaceId: WorkspaceId;
    userId: UserId;
    roleId: RoleId;
    roleName: string;
    /** The owner role cannot be edited or removed, and only it holds `workspace.delete`. */
    isOwner: boolean;
    displayName: string;
    /** What this person is called in the company's own sheet, if anything. Never an identity. */
    code?: string;
    capabilities: readonly Capability[];
    status: 'active' | 'suspended' | 'removed';
}

/** A company as the shell needs to name it. */
export interface WorkspaceSummary {
    id: WorkspaceId;
    name: string;
    slug: string;
    logoPath?: string;
    currency: string;
    locale: string;
    timezone: string;
    phoneCountry: string;
}

/**
 * Who is asking, and about which company.
 *
 * Every scoped read, every write and every server route takes one of these.
 * There is no ambient "current workspace" anywhere: a value that can be read
 * without being passed is a value that can be stale after a workspace switch,
 * and a stale workspace is the whole failure mode this product must not have.
 */
export interface TenantContext {
    workspace: WorkspaceSummary;
    membership: WorkspaceMembership;
}

/** Does this context hold the capability? The one place the UI asks. */
export const holds = (ctx: TenantContext | null | undefined, cap: Capability): boolean =>
    !!ctx && ctx.membership.status === 'active'
        && (ctx.membership.isOwner || ctx.membership.capabilities.includes(cap));

/**
 * Whether two contexts are the same company.
 *
 * Used to drop a response that arrived after the person switched companies.
 * Without it, a slow read issued against company A can land in company B's
 * state and be rendered there.
 */
export const sameWorkspace = (a: WorkspaceId | null | undefined, b: WorkspaceId | null | undefined): boolean =>
    !!a && !!b && a === b;
